import { useMemo, useState } from 'react';

// Phase 0.5 (ai-trust-hardening-plan): chronological trace of a queue run.
// One row per progress event: time, stage, duration since previous event, outcome,
// token cost. Rows expand to the full event payload (plan, validation, tool call
// arguments/results, dispositions). Failures are highlighted at the exact point in
// the sequence. Filters: all / errors / tool calls / attempts.

const stageTone = {
  routing: 'bg-sky-100 text-sky-800',
  understanding: 'bg-violet-100 text-violet-800',
  resolver_tool_call: 'bg-amber-100 text-amber-800',
  resolver_attempt: 'bg-indigo-100 text-indigo-800',
  human_review: 'bg-orange-100 text-orange-800',
  execution: 'bg-emerald-100 text-emerald-800',
  ai_unavailable: 'bg-red-100 text-red-800',
  nudge_copy: 'bg-teal-100 text-teal-800',
};

const statusTone = {
  started: 'bg-sky-100 text-sky-800',
  completed: 'bg-emerald-100 text-emerald-800',
  blocked: 'bg-red-100 text-red-800',
  info: 'bg-slate-100 text-slate-600',
};

function parseTime(value) {
  const time = Date.parse(value ?? '');
  return Number.isFinite(time) ? time : null;
}

function formatClock(value) {
  const time = parseTime(value);
  if (time === null) {
    return '—';
  }
  return new Date(time).toLocaleTimeString(undefined, { hour12: false });
}

function formatDuration(ms) {
  if (ms === null || !Number.isFinite(ms) || ms < 0) {
    return null;
  }
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }
  if (ms < 60_000) {
    return `${(ms / 1000).toFixed(1)}s`;
  }
  return `${Math.floor(ms / 60_000)}m ${Math.round((ms % 60_000) / 1000)}s`;
}

function formatTokens(count) {
  if (!Number.isFinite(count) || count <= 0) {
    return null;
  }
  return count >= 1000 ? `${(count / 1000).toFixed(1)}k tok` : `${count} tok`;
}

function isErrorEvent(event) {
  return (
    event.status === 'blocked' ||
    event.stage === 'ai_unavailable' ||
    event.executionOutcome === 'failed' ||
    Boolean(event.details && event.details.error)
  );
}

function eventDetailPayload(event) {
  const payload = {};
  if (event.details && Object.keys(event.details).length > 0) {
    payload.details = event.details;
  }
  if (event.toolCall) {
    payload.toolCall = event.toolCall;
  }
  if (event.tokenUsage) {
    payload.tokenUsage = event.tokenUsage;
  }
  if (event.transition) {
    payload.transition = event.transition;
  }
  return Object.keys(payload).length > 0 ? payload : null;
}

function TraceRow({ event, previousAt, isFirstError }) {
  const [copied, setCopied] = useState(false);
  const at = parseTime(event.at);
  const delta =
    at !== null && previousAt !== null ? formatDuration(at - previousAt) : null;
  const payload = eventDetailPayload(event);
  const error = isErrorEvent(event);
  const tokens = formatTokens(event.tokenUsage?.totalTokens);

  const copyPayload = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify({ ...event }, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard access can be denied; the row content remains visible either way.
    }
  };

  return (
    <li
      className={`rounded-[18px] border p-3 shadow-subtle ${
        error
          ? 'border-red-300 bg-red-50/60'
          : 'border-app-border/70 bg-app-surface/95'
      }`}
      id={isFirstError ? 'trace-first-error' : undefined}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs text-app-muted">{formatClock(event.at)}</span>
        {delta ? (
          <span className="font-mono text-xs text-app-muted/80">+{delta}</span>
        ) : null}
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
            stageTone[event.stage] || 'bg-slate-100 text-slate-600'
          }`}
        >
          {event.stage}
        </span>
        <span
          className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
            statusTone[event.status] || 'bg-slate-100 text-slate-600'
          }`}
        >
          {event.status}
        </span>
        {typeof event.attempt === 'number' ? (
          <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-xs font-medium text-indigo-700">
            attempt {event.attempt}
          </span>
        ) : null}
        {tokens ? (
          <span className="rounded-full bg-slate-50 px-2 py-0.5 text-xs font-medium text-slate-600">
            {tokens}
          </span>
        ) : null}
        {event.executionOutcome ? (
          <span
            className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
              event.executionOutcome === 'failed'
                ? 'bg-red-100 text-red-800'
                : 'bg-emerald-100 text-emerald-800'
            }`}
          >
            {event.executionOutcome}
          </span>
        ) : null}
      </div>
      <p className="m-0 mt-2 text-sm font-semibold text-app-ink">{event.title}</p>
      {event.summary ? (
        <p className="m-0 mt-1 text-sm leading-6 text-app-muted">{event.summary}</p>
      ) : null}
      {payload ? (
        <details className="mt-2">
          <summary className="cursor-pointer text-xs font-medium text-app-muted hover:text-app-ink">
            Full event payload
          </summary>
          <div className="mt-2">
            <button
              className="rounded-lg border border-app-border/70 px-2 py-1 text-xs text-app-muted hover:text-app-ink"
              onClick={copyPayload}
              type="button"
            >
              {copied ? 'Copied' : 'Copy as JSON'}
            </button>
            <pre className="mt-2 max-h-96 overflow-auto rounded-xl bg-slate-900/95 p-3 text-xs leading-5 text-slate-100">
              {JSON.stringify(payload, null, 2)}
            </pre>
          </div>
        </details>
      ) : null}
    </li>
  );
}

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'errors', label: 'Errors' },
  { id: 'tools', label: 'Tool calls' },
  { id: 'attempts', label: 'Attempts' },
  { id: 'execution', label: 'Execution' },
];

function matchesFilter(event, filter) {
  if (filter === 'errors') {
    return isErrorEvent(event);
  }
  if (filter === 'tools') {
    return event.stage === 'resolver_tool_call' || Boolean(event.toolCall);
  }
  if (filter === 'attempts') {
    return event.stage === 'resolver_attempt';
  }
  if (filter === 'execution') {
    return event.stage === 'execution';
  }
  return true;
}

function QueueTraceTimeline({ events }) {
  const [filter, setFilter] = useState('all');

  const ordered = useMemo(
    () =>
      [...(events || [])].sort((left, right) => {
        // Trace entries carry an authoritative sequence; fall back to timestamps for
        // progress.events mirrors that lack it.
        if (typeof left.sequence === 'number' && typeof right.sequence === 'number') {
          return left.sequence - right.sequence;
        }
        return (parseTime(left.at) ?? 0) - (parseTime(right.at) ?? 0);
      }),
    [events]
  );

  const summary = useMemo(() => {
    const attempts = new Set();
    let toolCalls = 0;
    let tokens = 0;
    let errors = 0;
    ordered.forEach((event) => {
      if (typeof event.attempt === 'number') {
        attempts.add(event.attempt);
      }
      if (event.stage === 'resolver_tool_call' || event.toolCall) {
        toolCalls += 1;
      }
      tokens += event.tokenUsage?.totalTokens ?? 0;
      if (isErrorEvent(event)) {
        errors += 1;
      }
    });
    const first = parseTime(ordered[0]?.at);
    const last = parseTime(ordered[ordered.length - 1]?.at);
    return {
      attempts: attempts.size,
      toolCalls,
      tokens,
      errors,
      duration: first !== null && last !== null ? formatDuration(last - first) : null,
    };
  }, [ordered]);

  const visible = useMemo(
    () => ordered.filter((event) => matchesFilter(event, filter)),
    [ordered, filter]
  );

  const firstErrorIndex = visible.findIndex((event) => isErrorEvent(event));

  if (!ordered.length) {
    return null;
  }

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 text-xs font-medium text-app-muted">
        <span>{ordered.length} events</span>
        {summary.attempts > 0 ? <span>{summary.attempts} attempts</span> : null}
        {summary.toolCalls > 0 ? <span>{summary.toolCalls} tool calls</span> : null}
        {summary.tokens > 0 ? <span>{formatTokens(summary.tokens)} total</span> : null}
        {summary.duration ? <span>{summary.duration} elapsed</span> : null}
        {summary.errors > 0 ? (
          <span className="rounded-full bg-red-100 px-2 py-0.5 font-semibold text-red-800">
            {summary.errors} issue{summary.errors === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        {FILTERS.map((entry) => (
          <button
            className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
              filter === entry.id
                ? 'border-app-ink bg-app-ink text-app-surface'
                : 'border-app-border/70 text-app-muted hover:text-app-ink'
            }`}
            key={entry.id}
            onClick={() => setFilter(entry.id)}
            type="button"
          >
            {entry.label}
          </button>
        ))}
      </div>
      <ol className="mt-4 grid list-none gap-2 p-0">
        {visible.map((event, index) => (
          <TraceRow
            event={event}
            isFirstError={index === firstErrorIndex}
            key={event.id || `${event.at}-${index}`}
            previousAt={index > 0 ? parseTime(visible[index - 1].at) : null}
          />
        ))}
      </ol>
    </div>
  );
}

export default QueueTraceTimeline;
